import { useState, useRef, useEffect, type MutableRefObject } from "react";
import mapboxgl from "mapbox-gl";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import type { Map as MapboxMap, Marker } from "mapbox-gl";
import { useUpdateAddress } from "../../api/addresses/hooks";
import { updateAddressPosition } from "../layers";

export interface UseMovePointReturn {
    isMoving: boolean;
    movingAddressId: string | null;
    markerRef: MutableRefObject<Marker | null>;
    startMove: (addressId: string, lat: number, lon: number) => void;
    cancelMove: () => void;
    confirmMove: (newLat: number, newLon: number) => Promise<void>;
}

export const useMovePoint = (map: MapboxMap | null): UseMovePointReturn => {
    const { t } = useTranslation();
    const [isMoving, setIsMoving] = useState(false);
    const [movingAddressId, setMovingAddressId] = useState<string | null>(null);
    const markerRef = useRef<Marker | null>(null);
    const updateAddress = useUpdateAddress();

    // Cleanup marker on unmount
    useEffect(() => {
        return () => {
            if (markerRef.current) {
                markerRef.current.remove();
                markerRef.current = null;
            }
        };
    }, []);

    const startMove = (addressId: string, lat: number, lon: number) => {
        if (!map) return;

        // Clean up any existing marker
        if (markerRef.current) {
            markerRef.current.remove();
        }

        // Create a draggable marker at the current position
        const marker = new mapboxgl.Marker({
            draggable: true,
            color: "#3b82f6"
        })
            .setLngLat([lon, lat])
            .addTo(map);

        markerRef.current = marker;
        setIsMoving(true);
        setMovingAddressId(addressId);

        // Note: Move is confirmed manually via confirmMove button, not on dragend
    };

    const cancelMove = () => {
        if (markerRef.current) {
            markerRef.current.remove();
            markerRef.current = null;
        }
        setIsMoving(false);
        setMovingAddressId(null);
    };

    const confirmMove = async (newLat: number, newLon: number) => {
        if (!movingAddressId || !map) return;

        // Update the address position on the map immediately for instant visual feedback
        updateAddressPosition(map, movingAddressId, newLat, newLon);

        try {
            await updateAddress.mutateAsync({
                id: movingAddressId,
                request: {
                    latitude: newLat,
                    longitude: newLon
                }
            });

            // Clean up marker after successful update
            if (markerRef.current) {
                markerRef.current.remove();
                markerRef.current = null;
            }
            setIsMoving(false);
            setMovingAddressId(null);
            toast.success(t("map.moveAddress.success"));
        } catch (error) {
            console.error("Failed to move address:", error);
            const errorMessage = error instanceof Error ? error.message : t("map.moveAddress.error");
            toast.error(errorMessage);
            // Revert the visual update on error by invalidating queries
            // The address will revert to its original position when data is refetched
            // Keep marker visible on error so user can retry
        }
    };

    // Handle Escape key to cancel move
    useEffect(() => {
        if (!isMoving) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                cancelMove();
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isMoving]);

    return {
        isMoving,
        movingAddressId,
        markerRef,
        startMove,
        cancelMove,
        confirmMove
    };
};